import { Fetcher } from '@this/src/util';
import React from 'react';
import { Redirect } from 'react-router-dom';
import { Link } from '@this/shared/ui/navigations/link';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { reportError } from '@this/lib/bugsnag';
import { TextField } from '../../shared/form_elements/form_elements';

import User from '../../../domain/user/user';
import type UserJson from '../../../domain/user/user_json';

import { createColors } from '../../shared/ui/theme/tokens/primitive-tokens/colors';
import OneTimePasswordForm from '../../user_signin/one_time_password_form';
import type { OneTimePasswordRestartReason } from '../../user_signin/one_time_password';
import { ONE_TIME_PASSWORD_MESSAGES, responseErrorCode } from '../../user_signin/one_time_password';
import PasswordResetForm from './password_reset_form';

const brandColors = createColors(1).brand;

const PASSWORD_PATH = '/aiexpense/users/auth/password';
const ONE_TIME_PASSWORD_PATH = '/aiexpense/users/auth/one_time_password';
const ONE_TIME_PASSWORD_RESEND_PATH = '/aiexpense/users/auth/one_time_password/resend';

type PasswordResponse = UserJson | { type: 'one_time_password' };

const isOneTimePasswordRequired = (response: PasswordResponse): response is { type: 'one_time_password' } =>
  'type' in response && response.type === 'one_time_password';

interface Props {
  user: User | null | undefined;
  onSignedIn: (user: User) => void;
}

interface State {
  email: string;
  password: string;
  submitting: boolean;
  errors: {
    email?: string;
    password?: string;
  };
  submitError: string | null;
  succeed: boolean;
  showPasswordResetForm: boolean;
  showOneTimePasswordForm: boolean;
  redirectPath: string | null;
}

class Signin extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      submitting: false,
      errors: {},
      submitError: null,
      succeed: false,
      showPasswordResetForm: false,
      showOneTimePasswordForm: false,
      redirectPath: null
    };
  }

  handleEmailChange(email: string) {
    this.setState({ email });
  }

  handlePasswordChange(password: string) {
    this.setState({ password });
  }

  async handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      this.setState({ submitting: true, errors: {}, submitError: null });
      const args = {
        email: this.state.email,
        password: this.state.password
      };
      const response = await Fetcher.post<PasswordResponse>(PASSWORD_PATH, args);
      if (isOneTimePasswordRequired(response)) {
        this.setState({ submitting: false, showOneTimePasswordForm: true });
        return;
      }
      this.handleSignedIn(response);
    } catch (e) {
      const errorCode = responseErrorCode(e);
      if (e.response?.status === 400) {
        this.setState({ submitting: false, errors: e.response.data.errors });
      } else if (errorCode === 'MailDeliveryFailed' || errorCode === 'SessionExpired') {
        this.setState({ submitting: false, submitError: ONE_TIME_PASSWORD_MESSAGES[errorCode] });
      } else if (e.response?.status === 401) {
        this.setState({ submitting: false, submitError: 'メールアドレスかパスワードが違います' });
      } else {
        this.setState({
          submitting: false,
          submitError: '通信エラーが発生しました。時間をおいて再度お試しください。'
        });
      }
    }
  }

  handleSignedIn = (response: UserJson) => {
    const user = new User(response);
    this.setState({ submitting: false, succeed: true, redirectPath: response.redirect_path ?? null });
    this.props.onSignedIn(user);
  };

  handleOneTimePasswordRestart = (reason: OneTimePasswordRestartReason | null) => {
    this.setState({
      showOneTimePasswordForm: false,
      password: '',
      submitError: reason ? ONE_TIME_PASSWORD_MESSAGES[reason] : null
    });
  };

  handleShowPasswordResetForm = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({ showPasswordResetForm: true });
  };

  navigateToSignin = () => {
    this.setState({ showPasswordResetForm: false });
  };

  render() {
    try {
      const styles = getStyles();
      const { user } = this.props;
      const {
        email,
        password,
        submitting,
        succeed,
        errors,
        submitError,
        showPasswordResetForm,
        showOneTimePasswordForm,
        redirectPath
      } = this.state;
      return (
        // NOTE: TextField の色はSCSSカスケード（.expenses-users-signup スコープ）で制御するためクラスを保持
        <div className="expenses-users-signup" style={styles.container}>
          <div style={styles.content}>
            {redirectPath ? (
              <Redirect to={redirectPath} />
            ) : user || succeed ? (
              <Redirect to="/aiexpense/report_items" />
            ) : showPasswordResetForm ? (
              <PasswordResetForm navigateToBack={this.navigateToSignin} />
            ) : showOneTimePasswordForm ? (
              <OneTimePasswordForm<UserJson>
                verifyPath={ONE_TIME_PASSWORD_PATH}
                resendPath={ONE_TIME_PASSWORD_RESEND_PATH}
                onVerified={this.handleSignedIn}
                onRestart={this.handleOneTimePasswordRestart}
              />
            ) : (
              <form onSubmit={e => this.handleSubmit(e)}>
                <TextField
                  label="メールアドレス（必須）"
                  value={email}
                  example="例：yamada@aitravel.jp"
                  error={errors.email}
                  onChange={value => this.handleEmailChange(value)}
                />
                <TextField
                  label="パスワード（必須）"
                  value={password}
                  example=""
                  type="password"
                  error={errors.password}
                  onChange={value => this.handlePasswordChange(value)}
                />
                {submitting ? <Loading /> : <input type="submit" value="ログイン" />}
                {submitError && <div className="error">{submitError}</div>}
                <div style={styles.utils}>
                  <a href="#" style={styles.link} onClick={this.handleShowPasswordResetForm}>
                    パスワードをお忘れですか？
                  </a>
                </div>
              </form>
            )}
          </div>
          <div style={styles.footer}>
            <Link href="/aiexpense/users/sign_up">新規登録はこちら</Link>
          </div>
        </div>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const getStyles = () => ({
  container: {
    maxWidth: '600px',
    width: '100%',
    margin: '50px auto'
  },
  content: {
    height: 'fit-content' as const,
    padding: '20px',
    background: 'white',
    fontSize: '16px',
    fontWeight: 'bold' as const
  },
  utils: {
    fontWeight: 'normal' as const,
    marginTop: '10px'
  },
  footer: {
    marginTop: '10px',
    textAlign: 'center' as const
  },
  link: {
    color: brandColors[500]
  }
});

export default Signin;
